import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon, XCircleIcon, SparklesIcon, PaperAirplaneIcon,
  Cog6ToothIcon, ClockIcon, ExclamationTriangleIcon, ArrowPathIcon,
  EyeIcon, TrashIcon,
} from '@heroicons/react/24/outline';

const C = {
  teal:'#00B4D8', success:'#10B981', warning:'#F59E0B', error:'#EF4444',
  dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', purple:'#8B5CF6',
  tgBlue:'#0088CC', igGrad:'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
};

const TOPICS = [
  { value:'general',      label:'📢 General announcement' },
  { value:'course_promo', label:'🎓 Course promotion' },
  { value:'achievement',  label:'🏆 Student achievement' },
  { value:'tips',         label:'💡 Learning tips' },
  { value:'ielts',        label:'📝 IELTS preparation' },
  { value:'mock_test',    label:'🧪 Mock test promotion' },
  { value:'reminder',     label:'⏰ Study reminder' },
  { value:'event',        label:'🗓 Event announcement' },
  { value:'custom',       label:'✏️ Custom (type your own)' },
];
const TONES = [
  { value:'friendly',     label:'😊 Friendly' },
  { value:'professional', label:'💼 Professional' },
  { value:'urgent',       label:'🔥 Urgent' },
  { value:'fun',          label:'🎉 Fun' },
];
const PLATFORMS_SEND = [
  { value:'telegram',  label:'Telegram' },
  { value:'instagram', label:'Instagram' },
  { value:'both',      label:'Both channels' },
];

/* ── Channel setup card ─────────────────────────────────────────────────────── */
function ChannelCard({ icon, name, color, connected, subtitle, children, onDisconnect, disconnecting }) {
  return (
    <div className="card" style={{ padding:24 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:44, height:44, borderRadius:12, background:color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>
            {icon}
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:15 }}>{name}</div>
            <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>{subtitle}</div>
          </div>
        </div>
        {connected ? (
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, fontWeight:700, color:C.success, background:'#D1FAE5', padding:'4px 12px', borderRadius:20 }}>
              <CheckCircleIcon style={{ width:14, height:14 }} /> Connected
            </span>
            <button onClick={onDisconnect} disabled={disconnecting}
              style={{ fontSize:12, color:C.error, background:'#FEF2F2', border:`1px solid #FECACA`, borderRadius:8, padding:'4px 12px', cursor:'pointer', fontWeight:600 }}>
              {disconnecting ? '…' : 'Disconnect'}
            </button>
          </div>
        ) : (
          <span style={{ fontSize:12, fontWeight:600, color:C.grey, background:'#F3F4F6', padding:'4px 12px', borderRadius:20 }}>Not connected</span>
        )}
      </div>
      {children}
    </div>
  );
}

/* ── Telegram setup ─────────────────────────────────────────────────────────── */
function TelegramSetup({ channels, onRefresh }) {
  const [token, setToken] = useState('');
  const [channelId, setChannelId] = useState('');
  const [loading, setLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const connect = async () => {
    if (!token.trim() || !channelId.trim()) return toast.error('Both fields are required');
    setLoading(true);
    try {
      const res = await api.post('/api/admin/marketing/telegram/connect', { bot_token: token, channel_id: channelId });
      toast.success(res.data.message);
      setToken(''); setChannelId('');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Connection failed');
    } finally { setLoading(false); }
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try { await api.post('/api/admin/marketing/telegram/disconnect'); onRefresh(); }
    catch { toast.error('Failed to disconnect'); }
    finally { setDisconnecting(false); }
  };

  return (
    <ChannelCard icon="✈️" name="Telegram" color="#0088CC22" subtitle="Broadcast to your Telegram channel"
      connected={channels?.telegram?.connected} onDisconnect={disconnect} disconnecting={disconnecting}>
      {channels?.telegram?.connected ? (
        <div style={{ background:'#F0FDF4', borderRadius:10, padding:14, fontSize:13 }}>
          <div style={{ color:C.grey, marginBottom:4 }}>Connected channel</div>
          <div style={{ fontWeight:700, fontFamily:'monospace' }}>{channels.telegram.channel_id}</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div style={{ background:'#F0F9FF', borderRadius:10, padding:14, fontSize:12, color:'#0369A1' }}>
            <div style={{ fontWeight:700, marginBottom:6 }}>📋 How to connect:</div>
            <ol style={{ margin:0, paddingLeft:18, lineHeight:2 }}>
              <li>Open <strong>@BotFather</strong> on Telegram → /newbot</li>
              <li>Copy the bot token provided</li>
              <li>Add your bot to your channel as <strong>Administrator</strong></li>
              <li>Enter your channel as <strong>@yourchannel</strong> or the numeric ID</li>
            </ol>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:10 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Bot Token *</label>
              <input value={token} onChange={e => setToken(e.target.value)} placeholder="1234567890:ABCDEF..."
                style={{ width:'100%', padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, boxSizing:'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Channel ID *</label>
              <input value={channelId} onChange={e => setChannelId(e.target.value)} placeholder="@mychannel or -1001234..."
                style={{ width:'100%', padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, boxSizing:'border-box' }} />
            </div>
          </div>
          <button onClick={connect} disabled={loading}
            style={{ alignSelf:'flex-start', padding:'9px 22px', background:C.tgBlue, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', opacity:loading?0.7:1 }}>
            {loading ? '🔄 Verifying…' : '🔗 Connect & Verify'}
          </button>
        </div>
      )}
    </ChannelCard>
  );
}

/* ── Instagram setup ────────────────────────────────────────────────────────── */
function InstagramSetup({ channels, onRefresh }) {
  const [token, setToken] = useState('');
  const [pageId, setPageId] = useState('');
  const [loading, setLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const connect = async () => {
    if (!token.trim() || !pageId.trim()) return toast.error('Both fields are required');
    setLoading(true);
    try {
      const res = await api.post('/api/admin/marketing/instagram/connect', { access_token: token, page_id: pageId });
      toast.success(res.data.message);
      setToken(''); setPageId('');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Connection failed');
    } finally { setLoading(false); }
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try { await api.post('/api/admin/marketing/instagram/disconnect'); onRefresh(); }
    catch { toast.error('Failed to disconnect'); }
    finally { setDisconnecting(false); }
  };

  return (
    <ChannelCard icon="📸" name="Instagram" color="#E1306C22" subtitle="Post to your Instagram Business account"
      connected={channels?.instagram?.connected} onDisconnect={disconnect} disconnecting={disconnecting}>
      {channels?.instagram?.connected ? (
        <div style={{ background:'#FDF2F8', borderRadius:10, padding:14, fontSize:13 }}>
          <div style={{ color:C.grey, marginBottom:4 }}>Connected account</div>
          <div style={{ fontWeight:700 }}>@{channels.instagram.username}</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div style={{ background:'#FDF2F8', borderRadius:10, padding:14, fontSize:12, color:'#9D174D' }}>
            <div style={{ fontWeight:700, marginBottom:6 }}>📋 Requirements:</div>
            <ol style={{ margin:0, paddingLeft:18, lineHeight:2 }}>
              <li>You need an <strong>Instagram Business Account</strong> linked to a Facebook Page</li>
              <li>Create a <strong>Meta Developer App</strong> at developers.facebook.com</li>
              <li>Generate a <strong>Page Access Token</strong> with <code>instagram_basic</code> + <code>instagram_content_publish</code> permissions</li>
              <li>Find your <strong>Facebook Page ID</strong> in Page Settings → About</li>
            </ol>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:10 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Page Access Token *</label>
              <input value={token} onChange={e => setToken(e.target.value)} placeholder="EAABsbCS..."
                style={{ width:'100%', padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, boxSizing:'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Facebook Page ID *</label>
              <input value={pageId} onChange={e => setPageId(e.target.value)} placeholder="123456789012345"
                style={{ width:'100%', padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, boxSizing:'border-box' }} />
            </div>
          </div>
          <button onClick={connect} disabled={loading}
            style={{ alignSelf:'flex-start', padding:'9px 22px', background:'#E1306C', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', opacity:loading?0.7:1 }}>
            {loading ? '🔄 Verifying…' : '🔗 Connect Instagram'}
          </button>
        </div>
      )}
    </ChannelCard>
  );
}

/* ── Main Component ─────────────────────────────────────────────────────────── */
export default function AdminMarketing() {
  const [tenant, setTenant] = useState(null);
  const [marketingData, setMarketingData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Composer
  const [composeMsg, setComposeMsg] = useState('');
  const [sendPlatform, setSendPlatform] = useState('telegram');
  const [sending, setSending] = useState(false);
  const [posterUrl, setPosterUrl] = useState('');   // attached poster for the broadcast
  const [igContent, setIgContent] = useState('');   // Instagram version (when 'both' generated)

  // AI generator
  const [aiTopic, setAiTopic] = useState('general');
  const [aiCustom, setAiCustom] = useState('');      // free-typed custom prompt
  const [aiTone, setAiTone] = useState('friendly');
  const [aiPlatform, setAiPlatform] = useState('telegram');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState('');
  const [genCost, setGenCost] = useState(null);     // {call, month} AI cost info
  const [posterHeadline, setPosterHeadline] = useState('');
  const [posterSubtext, setPosterSubtext] = useState('');
  const [posterCta, setPosterCta] = useState('Book Now');
  const [makingPoster, setMakingPoster] = useState(false);

  // Interactive quiz (Telegram)
  const [quiz, setQuiz] = useState(null);   // {question, options[], correct_index, explanation}
  const [quizGen, setQuizGen] = useState(false);
  const [quizSending, setQuizSending] = useState(false);

  // AI usage cost + external image upload
  const [aiUsage, setAiUsage] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);

  // Bulk quiz + auto-schedule
  const [bulkCount, setBulkCount] = useState(10);
  const [bulkGen, setBulkGen] = useState(false);
  const [libTarget, setLibTarget] = useState(100);
  const [libBuilding, setLibBuilding] = useState(false);
  const [quizQueue, setQuizQueue] = useState(null);
  const [schedEnabled, setSchedEnabled] = useState(false);
  const [schedPerDay, setSchedPerDay] = useState(6);
  const [schedTz, setSchedTz] = useState(0);
  const [savingSched, setSavingSched] = useState(false);

  // Auto-marketing
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoFreq, setAutoFreq] = useState('weekly');
  const [savingAuto, setSavingAuto] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/api/admin/dashboard'),
      api.get('/api/admin/marketing'),
    ]).then(([dashRes, mkRes]) => {
      setTenant(dashRes.data?.tenant);
      setMarketingData(mkRes.data);
      setAutoEnabled(mkRes.data?.auto_marketing?.enabled || false);
      setAutoFreq(mkRes.data?.auto_marketing?.frequency || 'weekly');
    }).catch(() => toast.error('Failed to load marketing data'))
      .finally(() => setLoading(false));
    api.get('/api/admin/ai-usage').then(r => setAiUsage(r.data)).catch(() => {});
    loadQuizQueue();
  }, []);

  const loadQuizQueue = () => {
    api.get('/api/admin/marketing/quiz-queue').then(r => {
      setQuizQueue(r.data);
      if (r.data?.settings) {
        setSchedEnabled(!!r.data.settings.enabled);
        setSchedPerDay(r.data.settings.per_day || 6);
        setSchedTz(r.data.settings.tz_offset || 0);
      }
    }).catch(() => {});
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleBulkGenerate = async () => {
    if (aiTopic === 'custom' && !aiCustom.trim()) return toast.error('Type a theme for the quizzes');
    setBulkGen(true);
    try {
      const res = await api.post('/api/admin/marketing/quiz-bulk', {
        count: bulkCount, topic: aiTopic, custom_topic: aiTopic === 'custom' ? aiCustom : '',
      });
      toast.success(`${res.data.generated} quizzes queued${res.data.skipped_duplicates ? ` · ${res.data.skipped_duplicates} duplicates skipped` : ''}${res.data.first_posted ? ' · first posted now' : ''}`);
      loadQuizQueue();
      api.get('/api/admin/ai-usage').then(r => setAiUsage(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Bulk generation failed');
    } finally { setBulkGen(false); }
  };

  const handleBuildLibrary = async () => {
    if (aiTopic === 'custom' && !aiCustom.trim()) return toast.error('Type a theme for the quizzes');
    setLibBuilding(true);
    try {
      await api.post('/api/admin/marketing/quiz-library', {
        target: libTarget, topic: aiTopic, custom_topic: aiTopic === 'custom' ? aiCustom : '',
      });
      toast.success(`Building up to ${libTarget} quizzes in the background — they'll appear in the queue over the next couple of minutes.`);
      // Poll the queue so the count visibly grows as batches land (~30s each).
      let ticks = 0;
      const poll = setInterval(() => {
        ticks += 1;
        loadQuizQueue();
        api.get('/api/admin/ai-usage').then(r => setAiUsage(r.data)).catch(() => {});
        if (ticks >= 40) { clearInterval(poll); setLibBuilding(false); }
      }, 8000);
      return; // keep the button in "building" state while polling
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Library build failed');
      setLibBuilding(false);
    }
  };

  const saveSchedule = async (override) => {
    setSavingSched(true);
    try {
      const payload = { enabled: schedEnabled, per_day: schedPerDay, tz_offset: schedTz, ...override };
      await api.put('/api/admin/marketing/quiz-schedule', payload);
      toast.success('Schedule saved');
      loadQuizQueue();
    } catch (e) {
      toast.error('Could not save schedule');
    } finally { setSavingSched(false); }
  };

  const deleteQueued = async (id) => {
    try { await api.delete(`/api/admin/marketing/quiz-queue/${id}`); loadQuizQueue(); }
    catch { toast.error('Could not remove'); }
  };

  const handleGenerate = async () => {
    if (aiTopic === 'custom' && !aiCustom.trim()) return toast.error('Type what you want the post to be about');
    setGenerating(true);
    setGenerated('');
    try {
      const res = await api.post('/api/admin/marketing/generate', {
        topic: aiTopic, custom_topic: aiTopic === 'custom' ? aiCustom : '',
        tone: aiTone, platform: aiPlatform,
      });
      setGenerated(res.data.content);
      if (res.data.cost_usd != null) setGenCost({ call: res.data.cost_usd, month: res.data.month_cost_usd });
      api.get('/api/admin/ai-usage').then(r => setAiUsage(r.data)).catch(() => {});
      // Pre-fill a SHORT poster headline + supporting line from the content.
      const clean = (res.data.content || '').replace(/[*_#>`]/g, '')
        .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}←-⇿️]/gu, '').trim();
      const sentences = clean.split(/(?<=[.!?])\s+/).filter(s => s.trim());
      setPosterHeadline((sentences[0] || clean).slice(0, 70).trim());
      setPosterSubtext((sentences.slice(1, 3).join(' ')).slice(0, 160).trim());
      toast.success('Content generated!');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const handleMakePoster = async () => {
    const headline = (posterHeadline || '').trim();
    if (!headline) return toast.error('Enter a short headline for the poster');
    setMakingPoster(true);
    try {
      const res = await api.post('/api/admin/marketing/generate-image', {
        headline, subtext: posterSubtext, cta: posterCta || 'Book Now',
      });
      setPosterUrl(res.data.image_url);
      toast.success('Poster created & attached');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Poster generation failed');
    } finally { setMakingPoster(false); }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploadingImg(true);
    try {
      const res = await api.post('/api/admin/marketing/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPosterUrl(res.data.image_url);
      toast.success('Image uploaded & attached');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Upload failed');
    } finally { setUploadingImg(false); }
  };

  const handleGenerateQuiz = async () => {
    if (aiTopic === 'custom' && !aiCustom.trim()) return toast.error('Type a theme for the quiz');
    setQuizGen(true);
    try {
      const res = await api.post('/api/admin/marketing/generate-quiz', {
        topic: aiTopic, custom_topic: aiTopic === 'custom' ? aiCustom : '',
      });
      setQuiz(res.data.quiz);
      api.get('/api/admin/ai-usage').then(r => setAiUsage(r.data)).catch(() => {});
      loadQuizQueue();
      toast.success(res.data.duplicate ? 'Quiz generated (similar one already in queue)' : 'Quiz generated & saved');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Quiz generation failed');
    } finally { setQuizGen(false); }
  };

  const updateQuizOption = (i, val) => {
    setQuiz(q => ({ ...q, options: q.options.map((o, idx) => idx === i ? val : o) }));
  };

  const handleSendQuiz = async () => {
    if (!quiz?.question?.trim()) return toast.error('Quiz question is empty');
    if (!marketingData?.channels?.telegram?.connected) return toast.error('Connect Telegram to send quizzes');
    setQuizSending(true);
    try {
      await api.post('/api/admin/marketing/broadcast-quiz', {
        queue_id: quiz.id, question: quiz.question, options: quiz.options,
        correct_index: quiz.correct_index, explanation: quiz.explanation,
      });
      toast.success('✅ Quiz posted to Telegram!');
      setQuiz(null);
      loadQuizQueue();
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to send quiz');
    } finally { setQuizSending(false); }
  };

  const useGenerated = () => {
    const raw = generated || '';
    let tg = raw.trim(), ig = '';
    // 'Both' output contains two versions split by a divider — separate them so
    // each channel gets only its own clean copy (no divider, no cross-posting).
    if (/---\s*INSTAGRAM\s*---/i.test(raw)) {
      const [a, b] = raw.split(/---\s*INSTAGRAM\s*---/i);
      tg = (a || '').replace(/^\s*TELEGRAM\s*:?\s*/i, '').trim();
      ig = (b || '').replace(/^\s*INSTAGRAM\s*:?\s*/i, '').trim();
    }
    if (aiPlatform === 'instagram') { tg = raw.trim(); ig = ''; }
    setComposeMsg(tg);
    setIgContent(ig);
    setSendPlatform(aiPlatform === 'both' ? 'both' : aiPlatform);
    setGenerated('');
    document.getElementById('compose-section')?.scrollIntoView({ behavior: 'smooth' });
    toast.success('Content moved to composer');
  };

  const handleSend = async () => {
    if (!composeMsg.trim()) return toast.error('Message is empty');
    const channels = marketingData?.channels;
    if (sendPlatform === 'telegram' && !channels?.telegram?.connected) return toast.error('Telegram not connected');
    if (sendPlatform === 'instagram' && !channels?.instagram?.connected) return toast.error('Instagram not connected');
    if (sendPlatform === 'both' && !channels?.telegram?.connected && !channels?.instagram?.connected)
      return toast.error('No channels connected');

    setSending(true);
    try {
      const igText = igContent || composeMsg;
      const res = await api.post('/api/admin/marketing/broadcast', {
        // Telegram gets composeMsg; Instagram gets its own version when one exists.
        content: sendPlatform === 'instagram' ? igText : composeMsg,
        instagram_content: igText,
        platform: sendPlatform, ai_generated: false,
        image_url: posterUrl || '',
      });
      const results = res.data.results || {};
      if (results.telegram?.success) toast.success('✅ Sent to Telegram!');
      if (results.telegram?.error) toast.error(`Telegram: ${results.telegram.error}`);
      if (results.instagram?.success) toast.success('✅ Posted to Instagram!');
      if (results.instagram?.error) toast.error(`Instagram: ${results.instagram.error}`);
      setComposeMsg('');
      setPosterUrl('');
      setIgContent('');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Send failed');
    } finally { setSending(false); }
  };

  const handleSaveAuto = async () => {
    setSavingAuto(true);
    try {
      await api.put('/api/admin/marketing/auto', { enabled: autoEnabled, frequency: autoFreq });
      toast.success(autoEnabled ? `Auto-marketing enabled (${autoFreq})` : 'Auto-marketing disabled');
      fetchData();
    } catch { toast.error('Failed to save settings'); }
    finally { setSavingAuto(false); }
  };

  const stats = marketingData?.stats || {};
  const channels = marketingData?.channels || {};
  const history = marketingData?.history || [];
  const connectedCount = (channels?.telegram?.connected ? 1 : 0) + (channels?.instagram?.connected ? 1 : 0);

  return (
    <Layout role="tenant_admin" tenantColor={tenant?.primary_colour} tenantName={tenant?.school_name}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div>
          <h2 style={{ margin:'0 0 4px', fontSize:22, fontWeight:700 }}>Marketing</h2>
          <p style={{ margin:0, color:C.grey, fontSize:14 }}>Connect channels, generate AI content, auto-broadcast to students</p>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <div style={{ padding:'8px 16px', background: connectedCount > 0 ? '#D1FAE5' : '#F3F4F6', borderRadius:20, fontSize:13, fontWeight:700, color: connectedCount > 0 ? C.success : C.grey }}>
            {connectedCount} channel{connectedCount !== 1 ? 's' : ''} connected
          </div>
          <div style={{ padding:'8px 16px', background:'#EFF6FF', borderRadius:20, fontSize:13, fontWeight:700, color:'#2563EB' }}>
            {stats.student_count || 0} students
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:16, marginBottom:24 }}>
        {[
          { label:'Total Students', value: stats.student_count || 0, icon:'👥', color:C.teal },
          { label:'Active This Week', value: stats.active_students_week || 0, icon:'⚡', color:C.success },
          { label:'Lessons This Week', value: stats.lessons_this_week || 0, icon:'📚', color:C.purple },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding:'16px 20px', display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:40, height:40, borderRadius:10, background:s.color+'20', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>{s.icon}</div>
            <div>
              <div style={{ fontSize:22, fontWeight:800, color:s.color }}>{s.value}</div>
              <div style={{ fontSize:12, color:C.grey }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── AI Usage Cost ── */}
      <div className="card" style={{ padding:'18px 22px', marginBottom:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', flexWrap:'wrap', gap:8, marginBottom:14 }}>
          <h3 style={{ fontSize:15, fontWeight:700, margin:0 }}>💡 Your AI Usage Cost</h3>
          <span style={{ fontSize:12, color:C.grey }}>Each AI generation (content, poster headline, quiz) uses Claude and is billed to your monthly invoice.</span>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(130px, 1fr))', gap:14 }}>
          {[
            ['Today', aiUsage?.today], ['This week', aiUsage?.this_week],
            ['This month', aiUsage?.this_month], ['All time', aiUsage?.all_time],
          ].map(([label, val]) => (
            <div key={label} style={{ border:`1px solid ${C.border}`, borderRadius:10, padding:'12px 14px', background: label==='This month' ? '#F0FDFF' : '#fff' }}>
              <div style={{ fontSize:11, color:C.grey, fontWeight:600, marginBottom:4 }}>{label}</div>
              <div style={{ fontSize: label==='This month' ? 22 : 18, fontWeight:800, color: (val||0) > 0 ? C.dark : C.grey }}>
                ${Number(val || 0).toFixed(4)}
              </div>
            </div>
          ))}
        </div>
        {aiUsage?.by_purpose?.length > 0 && (
          <div style={{ marginTop:12, fontSize:12, color:C.grey }}>
            This month by type: {aiUsage.by_purpose.map(p => `${p.purpose.replace(/_/g,' ')} $${p.cost.toFixed(4)} (${p.calls})`).join(' · ')}
          </div>
        )}
      </div>

      {/* ── Channel Connections ── */}
      <div style={{ marginBottom:8 }}>
        <h3 style={{ fontSize:15, fontWeight:700, margin:'0 0 4px' }}>Channel Connections</h3>
        <p style={{ margin:'0 0 16px', fontSize:13, color:C.grey }}>Each school connects its own accounts — credentials are stored securely per partner</p>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:20, marginBottom:28 }}>
        <TelegramSetup channels={channels} onRefresh={fetchData} />
        <InstagramSetup channels={channels} onRefresh={fetchData} />
      </div>

      {/* ── AI Content Generator ── */}
      <div className="card" style={{ padding:24, marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:'#EDE9FE', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <SparklesIcon style={{ width:18, height:18, color:C.purple }} />
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:15 }}>AI Content Generator</div>
            <div style={{ fontSize:12, color:C.grey }}>Generate professional marketing content instantly</div>
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:16 }}>
          <div>
            <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Topic</label>
            <select value={aiTopic} onChange={e => setAiTopic(e.target.value)}
              style={{ width:'100%', padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, background:'#fff' }}>
              {TOPICS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Tone</label>
            <select value={aiTone} onChange={e => setAiTone(e.target.value)}
              style={{ width:'100%', padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, background:'#fff' }}>
              {TONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Platform</label>
            <select value={aiPlatform} onChange={e => setAiPlatform(e.target.value)}
              style={{ width:'100%', padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, background:'#fff' }}>
              <option value="telegram">Telegram</option>
              <option value="instagram">Instagram</option>
              <option value="both">Both (2 versions)</option>
            </select>
          </div>
        </div>

        {aiTopic === 'custom' && (
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12, fontWeight:600, display:'block', marginBottom:4 }}>Your prompt — describe the post you want</label>
            <textarea value={aiCustom} onChange={e => setAiCustom(e.target.value)}
              placeholder="e.g. Announce a 20% discount on weekend IELTS mock tests this month, mention limited seats and how to book."
              style={{ width:'100%', minHeight:70, padding:'10px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, resize:'vertical', fontFamily:'inherit' }} />
          </div>
        )}

        <button onClick={handleGenerate} disabled={generating}
          style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 22px', background:C.purple, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:14, cursor:'pointer', marginBottom: generated ? 16 : 0, opacity:generating?0.7:1 }}>
          {generating ? <><ArrowPathIcon style={{ width:16, height:16 }} /> Generating…</> : <><SparklesIcon style={{ width:16, height:16 }} /> Generate Content</>}
        </button>

        {generated && (
          <div style={{ background:'#F9F7FF', border:`1px solid #DDD6FE`, borderRadius:10, padding:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <span style={{ fontSize:13, fontWeight:700, color:C.purple }}>✨ AI Generated</span>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={useGenerated}
                  style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 14px', background:C.success, color:'#fff', border:'none', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                  <PaperAirplaneIcon style={{ width:12, height:12 }} /> Use this
                </button>
                <button onClick={() => setGenerated('')}
                  style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 10px', background:'#F3F4F6', border:`1px solid ${C.border}`, borderRadius:6, fontSize:12, cursor:'pointer' }}>
                  <TrashIcon style={{ width:12, height:12 }} />
                </button>
              </div>
            </div>
            <pre style={{ margin:0, fontSize:13, lineHeight:1.7, whiteSpace:'pre-wrap', fontFamily:'inherit', color:C.dark }}>{generated}</pre>
            {genCost && (
              <div style={{ marginTop:12, paddingTop:10, borderTop:`1px solid #DDD6FE`, fontSize:12, color:C.grey, display:'flex', gap:16, flexWrap:'wrap' }}>
                <span>💰 This generation: <b style={{ color:C.dark }}>${Number(genCost.call).toFixed(4)}</b></span>
                <span>📅 AI usage this month: <b style={{ color:C.dark }}>${Number(genCost.month).toFixed(4)}</b></span>
                <span style={{ color:'#92400E' }}>Added to your monthly invoice as usage.</span>
              </div>
            )}
          </div>
        )}

        {/* Branded poster generator */}
        {(generated || posterUrl) && (
          <div style={{ background:'#F0FDFF', border:`1px solid #BAE6FD`, borderRadius:10, padding:16, marginTop:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.dark, marginBottom:8 }}>🖼 Branded poster {' '}<span style={{ fontWeight:500, color:C.grey }}>(needed for Instagram; optional for Telegram)</span></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:8 }}>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:C.grey, display:'block', marginBottom:3 }}>Headline (short & punchy)</label>
                <input value={posterHeadline} onChange={e => setPosterHeadline(e.target.value)}
                  placeholder="e.g. Ace your IELTS with confidence"
                  style={{ width:'100%', padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, boxSizing:'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:C.grey, display:'block', marginBottom:3 }}>Supporting line (optional)</label>
                <input value={posterSubtext} onChange={e => setPosterSubtext(e.target.value)}
                  placeholder="e.g. Real exam conditions, instant score report"
                  style={{ width:'100%', padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, boxSizing:'border-box' }} />
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
                <div style={{ width:200 }}>
                  <label style={{ fontSize:11, fontWeight:600, color:C.grey, display:'block', marginBottom:3 }}>Button text</label>
                  <input value={posterCta} onChange={e => setPosterCta(e.target.value)}
                    placeholder="Book Now"
                    style={{ width:'100%', padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, boxSizing:'border-box' }} />
                </div>
                <button onClick={handleMakePoster} disabled={makingPoster}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', background:C.teal, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', opacity:makingPoster?0.7:1 }}>
                  {makingPoster ? <><ArrowPathIcon style={{ width:14, height:14 }} /> Creating…</> : <><SparklesIcon style={{ width:14, height:14 }} /> Generate Poster</>}
                </button>
              </div>
            </div>
            {posterUrl && (
              <div style={{ marginTop:12 }}>
                <img src={posterUrl} alt="poster preview" style={{ width:200, height:200, objectFit:'cover', borderRadius:10, border:`1px solid ${C.border}` }} />
                <div style={{ fontSize:12, color:C.success, fontWeight:600, marginTop:6 }}>✓ Attached — this image will be included when you broadcast.</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Interactive Quiz (Telegram) ── */}
      <div className="card" style={{ padding:24, marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:'#FEF3C7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>🧠</div>
          <div>
            <div style={{ fontWeight:700, fontSize:15 }}>Interactive Quiz</div>
            <div style={{ fontSize:12, color:C.grey }}>Members tap an answer, see ✅/❌ instantly, and get an explanation — posted as a Telegram quiz.</div>
          </div>
        </div>
        <div style={{ fontSize:12, color:C.grey, marginBottom:12 }}>Uses the <b>Topic</b> selected above{aiTopic==='custom' ? ' (your custom prompt)' : ''}.</div>

        <button onClick={handleGenerateQuiz} disabled={quizGen}
          style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 22px', background:'#F59E0B', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:14, cursor:'pointer', opacity:quizGen?0.7:1, marginBottom: quiz ? 16 : 0 }}>
          {quizGen ? <><ArrowPathIcon style={{ width:16, height:16 }} /> Generating…</> : <><SparklesIcon style={{ width:16, height:16 }} /> Generate Quiz</>}
        </button>

        {quiz && (
          <div style={{ background:'#FFFBEB', border:`1px solid #FDE68A`, borderRadius:10, padding:16 }}>
            <label style={{ fontSize:11, fontWeight:600, color:C.grey, display:'block', marginBottom:3 }}>Question</label>
            <input value={quiz.question} onChange={e => setQuiz(q => ({ ...q, question: e.target.value }))}
              style={{ width:'100%', padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, fontWeight:600, boxSizing:'border-box', marginBottom:12 }} />
            <label style={{ fontSize:11, fontWeight:600, color:C.grey, display:'block', marginBottom:5 }}>Options (select the correct one)</label>
            {quiz.options.map((opt, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                <input type="radio" name="correctopt" checked={quiz.correct_index === i}
                  onChange={() => setQuiz(q => ({ ...q, correct_index: i }))} style={{ cursor:'pointer' }} />
                <input value={opt} onChange={e => updateQuizOption(i, e.target.value)}
                  style={{ flex:1, padding:'7px 10px', border:`1px solid ${quiz.correct_index===i ? C.success : C.border}`, borderRadius:6, fontSize:13, background: quiz.correct_index===i ? '#ECFDF5' : '#fff' }} />
                {quiz.correct_index === i && <span style={{ fontSize:12, color:C.success, fontWeight:700 }}>✓ correct</span>}
              </div>
            ))}
            <label style={{ fontSize:11, fontWeight:600, color:C.grey, display:'block', margin:'10px 0 3px' }}>Explanation (shown after they answer)</label>
            <textarea value={quiz.explanation} onChange={e => setQuiz(q => ({ ...q, explanation: e.target.value }))}
              rows={2} style={{ width:'100%', padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }} />
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button onClick={handleSendQuiz} disabled={quizSending}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 18px', background:C.success, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', opacity:quizSending?0.7:1 }}>
                {quizSending ? <><ArrowPathIcon style={{ width:14, height:14 }} /> Sending…</> : <><PaperAirplaneIcon style={{ width:14, height:14 }} /> Send Quiz to Telegram</>}
              </button>
              <button onClick={async () => { if (quiz.id) { try { await api.delete(`/api/admin/marketing/quiz-queue/${quiz.id}`); } catch {} } setQuiz(null); loadQuizQueue(); }}
                style={{ padding:'9px 14px', background:'#F3F4F6', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, cursor:'pointer' }}>Discard</button>
            </div>
          </div>
        )}

        {/* Bulk generate + auto-schedule */}
        <div style={{ marginTop:18, paddingTop:18, borderTop:`1px solid ${C.border}` }}>
          <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>📅 Bulk generate &amp; auto-schedule</div>
          <div style={{ fontSize:12, color:C.grey, marginBottom:12 }}>
            Generate quizzes into the queue. With auto-post on, the scheduler drips them to your Telegram group on the hour (8am–9pm) at your chosen posts-per-day — no off-schedule posts. (With auto-post off, the first one posts immediately as a manual send.) When new quizzes run out, it recycles ones sent over 90 days ago — free, no repeats within 90 days.
          </div>

          <div style={{ display:'flex', gap:20, alignItems:'flex-end', flexWrap:'wrap', marginBottom:16 }}>
            <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:C.grey, display:'block', marginBottom:3 }}>Quick batch (5–50)</label>
                <input type="number" min={1} max={50} value={bulkCount}
                  onChange={e => setBulkCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  style={{ width:100, padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13 }} />
              </div>
              <button onClick={handleBulkGenerate} disabled={bulkGen || libBuilding}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 18px', background:C.purple, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', opacity:bulkGen?0.7:1 }}>
                {bulkGen ? <><ArrowPathIcon style={{ width:14, height:14 }} /> Generating…</> : <><SparklesIcon style={{ width:14, height:14 }} /> Generate Batch</>}
              </button>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'flex-end', borderLeft:`1px solid ${C.border}`, paddingLeft:20 }}>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:C.grey, display:'block', marginBottom:3 }}>Build library to (up to 400/click)</label>
                <input type="number" min={10} max={500} step={50} value={libTarget}
                  onChange={e => setLibTarget(Math.max(10, Math.min(500, parseInt(e.target.value) || 10)))}
                  style={{ width:120, padding:'9px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13 }} />
              </div>
              <button onClick={handleBuildLibrary} disabled={libBuilding || bulkGen}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 18px', background:C.dark, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', opacity:libBuilding?0.7:1 }}>
                {libBuilding ? <><ArrowPathIcon style={{ width:14, height:14 }} /> Building…</> : <>📚 Build Library</>}
              </button>
            </div>
          </div>

          {/* Schedule controls */}
          <div style={{ background:'#F8FAFC', border:`1px solid ${C.border}`, borderRadius:10, padding:16 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10, marginBottom:12 }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                <input type="checkbox" checked={schedEnabled}
                  onChange={e => { setSchedEnabled(e.target.checked); saveSchedule({ enabled: e.target.checked }); }} />
                Auto-post quizzes to Telegram
              </label>
              <div style={{ fontSize:12, color:C.grey, textAlign:'right' }}>
                {quizQueue ? <>📥 <b>{quizQueue.pending_count}</b> queued · <b>{quizQueue.posted_today}</b> sent today · <b>{quizQueue.posted_total || 0}</b> sent total</> : '…'}
                {quizQueue?.next_post && <div style={{ marginTop:2 }}>⏭ Next quiz: <b style={{ color:C.dark }}>{quizQueue.next_post}</b></div>}
                {quizQueue?.settings?.enabled && quizQueue?.pending_count === 0 && (quizQueue?.recyclable > 0
                  ? <div style={{ marginTop:2, color:C.success }}>♻️ Recycling {quizQueue.recyclable} quiz(zes) sent &gt;90 days ago (free).</div>
                  : <div style={{ marginTop:2, color:C.warning }}>Queue empty — generate a batch to resume.</div>)}
              </div>
            </div>
            <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:C.grey, display:'block', marginBottom:3 }}>Posts per day (1–13)</label>
                <input type="number" min={1} max={13} value={schedPerDay}
                  onChange={e => setSchedPerDay(Math.max(1, Math.min(13, parseInt(e.target.value) || 1)))}
                  style={{ width:100, padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13 }} />
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:600, color:C.grey, display:'block', marginBottom:3 }}>Timezone offset (UTC±)</label>
                <input type="number" min={-12} max={14} value={schedTz}
                  onChange={e => setSchedTz(Math.max(-12, Math.min(14, parseInt(e.target.value) || 0)))}
                  style={{ width:100, padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13 }} />
              </div>
              <button onClick={() => saveSchedule()} disabled={savingSched}
                style={{ padding:'8px 16px', background:C.teal, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>
                Save schedule
              </button>
            </div>
            <div style={{ fontSize:11, color:C.grey, marginTop:8 }}>
              Window is 8am–9pm in your local time. Set the offset to match your city (e.g. Tashkent = 5). {schedPerDay} per day ≈ one every {Math.max(1, Math.round(13 / schedPerDay))}h.
            </div>
          </div>

          {/* Queue preview */}
          {quizQueue?.pending?.length > 0 && (
            <div style={{ marginTop:14 }}>
              <div style={{ fontSize:12, fontWeight:600, color:C.grey, marginBottom:6 }}>Queued ({quizQueue.pending_count})</div>
              <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:220, overflowY:'auto' }}>
                {quizQueue.pending.map((q, i) => (
                  <div key={q.id} style={{ display:'flex', alignItems:'center', gap:10, background:'#fff', border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 12px' }}>
                    <span style={{ fontSize:12, color:C.grey, width:22 }}>{i + 1}.</span>
                    <span style={{ flex:1, fontSize:13, color:C.dark, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{q.question}</span>
                    <button onClick={() => deleteQueued(q.id)} style={{ padding:'3px 8px', background:'#fff', border:`1px solid ${C.border}`, borderRadius:6, fontSize:11, cursor:'pointer', color:C.error }}>Remove</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recently sent */}
          {quizQueue?.sent?.length > 0 && (
            <div style={{ marginTop:14 }}>
              <div style={{ fontSize:12, fontWeight:600, color:C.grey, marginBottom:6 }}>✅ Recently sent</div>
              <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:200, overflowY:'auto' }}>
                {quizQueue.sent.map((q) => (
                  <div key={q.id} style={{ display:'flex', alignItems:'center', gap:10, background:'#ECFDF5', border:`1px solid #A7F3D0`, borderRadius:8, padding:'8px 12px' }}>
                    <span style={{ color:C.success, fontSize:13 }}>✓</span>
                    <span style={{ flex:1, fontSize:13, color:C.dark, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{q.question}</span>
                    <span style={{ fontSize:11, color:C.grey, whiteSpace:'nowrap' }}>{q.sent_at}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Compose & Send ── */}
      <div id="compose-section" className="card" style={{ padding:24, marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:'#E0F7FA', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <PaperAirplaneIcon style={{ width:18, height:18, color:C.teal }} />
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:15 }}>Send Broadcast</div>
            <div style={{ fontSize:12, color:C.grey }}>Compose and send to your connected channels</div>
          </div>
        </div>

        {connectedCount === 0 && (
          <div style={{ display:'flex', alignItems:'center', gap:10, background:'#FEF3C7', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#92400E' }}>
            <ExclamationTriangleIcon style={{ width:16, height:16, flexShrink:0 }} />
            Connect at least one channel above before sending broadcasts.
          </div>
        )}

        <div style={{ display:'flex', gap:8, marginBottom:14 }}>
          {PLATFORMS_SEND.map(p => (
            <button key={p.value} onClick={() => setSendPlatform(p.value)}
              style={{ padding:'7px 16px', borderRadius:20, fontSize:13, fontWeight:600, cursor:'pointer', border:`1px solid ${sendPlatform===p.value ? C.teal : C.border}`, background:sendPlatform===p.value ? '#E0F7FA' : '#fff', color:sendPlatform===p.value ? C.teal : C.grey }}>
              {p.label}
            </button>
          ))}
        </div>

        <textarea value={composeMsg} onChange={e => setComposeMsg(e.target.value)} rows={5}
          placeholder={sendPlatform === 'instagram'
            ? 'Write your Instagram caption here... (tip: use emojis and hashtags)'
            : 'Write your Telegram message here... (HTML supported: <b>bold</b>, <i>italic</i>)'}
          style={{ width:'100%', padding:'12px', border:`1px solid ${C.border}`, borderRadius:10, fontSize:13, resize:'vertical', boxSizing:'border-box', lineHeight:1.6, marginBottom:12, fontFamily:'inherit' }} />

        {igContent && (sendPlatform === 'both') && (
          <div style={{ background:'#EEF2FF', border:`1px solid #C7D2FE`, borderRadius:8, padding:'9px 12px', marginBottom:12, fontSize:12, color:'#3730A3' }}>
            ℹ️ Two versions ready: the message above goes to <b>Telegram</b>; a separate Instagram caption (with hashtags) goes to <b>Instagram</b>.
          </div>
        )}

        {posterUrl ? (
          <div style={{ display:'flex', alignItems:'center', gap:12, background:'#F0FDFF', border:`1px solid #BAE6FD`, borderRadius:10, padding:10, marginBottom:12 }}>
            <img src={posterUrl} alt="attached poster" style={{ width:56, height:56, objectFit:'cover', borderRadius:8 }} />
            <div style={{ flex:1, fontSize:13, color:C.dark }}>Poster attached — will be posted as the image.</div>
            <button onClick={() => setPosterUrl('')} style={{ padding:'5px 10px', background:'#fff', border:`1px solid ${C.border}`, borderRadius:6, fontSize:12, cursor:'pointer' }}>Remove</button>
          </div>
        ) : (sendPlatform === 'instagram' || sendPlatform === 'both') && (
          <div style={{ display:'flex', alignItems:'center', gap:8, background:'#FEF3C7', borderRadius:8, padding:'9px 12px', marginBottom:12, fontSize:12, color:'#92400E' }}>
            <ExclamationTriangleIcon style={{ width:14, height:14, flexShrink:0 }} />
            Instagram requires an image — generate a poster above or upload your own, or it will be skipped.
          </div>
        )}

        {!posterUrl && (
          <div style={{ marginBottom:12 }}>
            <label style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'8px 14px', border:`1px dashed ${C.border}`, borderRadius:8, fontSize:13, fontWeight:600, color:C.dark, cursor:'pointer', background:'#fff' }}>
              {uploadingImg ? <ArrowPathIcon style={{ width:14, height:14 }} /> : '📎'} {uploadingImg ? 'Uploading…' : 'Upload your own image'}
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleUploadImage} disabled={uploadingImg} style={{ display:'none' }} />
            </label>
            <span style={{ fontSize:12, color:C.grey, marginLeft:10 }}>Made elsewhere (e.g. ChatGPT)? Upload it and paste your message above. JPG works best.</span>
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:12, color:C.grey }}>
            {composeMsg.length} characters
            {sendPlatform === 'telegram' && composeMsg.length > 4096 && (
              <span style={{ color:C.error, marginLeft:8 }}>⚠ Too long for Telegram (max 4096)</span>
            )}
          </div>
          <button onClick={handleSend} disabled={sending || !composeMsg.trim() || connectedCount === 0}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 24px', background:sending ? C.grey : C.teal, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:14, cursor:'pointer', opacity:(sending||!composeMsg.trim()||connectedCount===0)?0.6:1 }}>
            {sending ? <><ArrowPathIcon style={{ width:16, height:16 }} /> Sending…</> : <><PaperAirplaneIcon style={{ width:16, height:16 }} /> Send Now</>}
          </button>
        </div>
      </div>

      {/* ── Auto-Marketing ── */}
      <div className="card" style={{ padding:24, marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:'#FEF3C7', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Cog6ToothIcon style={{ width:18, height:18, color:C.warning }} />
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:15 }}>Auto-Marketing</div>
            <div style={{ fontSize:12, color:C.grey }}>Automatically generate & post AI content on a schedule</div>
          </div>
          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:13, color:C.grey }}>{autoEnabled ? 'Enabled' : 'Disabled'}</span>
            <div onClick={() => setAutoEnabled(!autoEnabled)}
              style={{ width:48, height:26, borderRadius:13, background:autoEnabled?C.success:'#D1D5DB', cursor:'pointer', position:'relative', transition:'background 0.2s' }}>
              <div style={{ position:'absolute', top:3, left:autoEnabled?25:3, width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
          </div>
        </div>

        {autoEnabled && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ background:'#FFFBEB', borderRadius:10, padding:14, fontSize:13, color:'#92400E' }}>
              <strong>How it works:</strong> SpeakIQ's AI will automatically generate relevant marketing messages based on your school's activity (new students, lessons completed, upcoming events) and post them to your connected channels on the selected schedule.
            </div>
            <div>
              <label style={{ fontSize:13, fontWeight:600, display:'block', marginBottom:6 }}>Posting frequency</label>
              <div style={{ display:'flex', gap:10 }}>
                {[['daily','📅 Daily'],['weekly','📆 Weekly'],['biweekly','🗓 Bi-weekly']].map(([v,l]) => (
                  <button key={v} onClick={() => setAutoFreq(v)}
                    style={{ padding:'8px 18px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', border:`2px solid ${autoFreq===v?C.warning:C.border}`, background:autoFreq===v?'#FEF9C3':'#fff', color:autoFreq===v?'#92400E':C.grey }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop:20, display:'flex', gap:10 }}>
          <button onClick={handleSaveAuto} disabled={savingAuto}
            style={{ padding:'9px 22px', background:C.warning, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', opacity:savingAuto?0.7:1 }}>
            {savingAuto ? 'Saving…' : 'Save Auto-Marketing Settings'}
          </button>
          {marketingData?.auto_marketing?.last_run && (
            <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:C.grey }}>
              <ClockIcon style={{ width:14, height:14 }} />
              Last run: {marketingData.auto_marketing.last_run.split('T')[0]}
            </div>
          )}
        </div>
      </div>

      {/* ── Broadcast History ── */}
      <div className="card" style={{ overflow:'hidden' }}>
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>Broadcast History</h3>
            <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>All sent messages across all channels</div>
          </div>
          <button onClick={fetchData} style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', background:'#F3F4F6', border:`1px solid ${C.border}`, borderRadius:8, fontSize:12, cursor:'pointer' }}>
            <ArrowPathIcon style={{ width:13, height:13 }} /> Refresh
          </button>
        </div>

        {loading ? (
          <div style={{ padding:'48px 24px', textAlign:'center', color:C.grey }}>Loading…</div>
        ) : history.length === 0 ? (
          <div style={{ padding:'48px 24px', textAlign:'center' }}>
            <div style={{ width:48, height:48, borderRadius:'50%', background:'#F3F4F6', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px', fontSize:22 }}>📭</div>
            <div style={{ fontSize:15, fontWeight:600, marginBottom:6 }}>No broadcasts yet</div>
            <div style={{ fontSize:13, color:C.grey }}>Connect a channel and send your first message above</div>
          </div>
        ) : (
          history.map((h, i) => (
            <div key={h.id} style={{ padding:'16px 24px', borderBottom:`1px solid ${C.border}`, background:i%2===0?'#fff':'#FAFAFA' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                    <span style={{ fontSize:13 }}>{h.platform === 'telegram' ? '✈️' : '📸'}</span>
                    <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20,
                      background: h.success ? '#D1FAE5' : '#FEE2E2',
                      color: h.success ? '#065F46' : C.error }}>
                      {h.success ? 'Sent' : 'Failed'}
                    </span>
                    {h.ai_generated && (
                      <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, background:'#EDE9FE', color:C.purple }}>
                        ✨ AI
                      </span>
                    )}
                    <span style={{ fontSize:11, color:C.grey }}>
                      {h.platform === 'telegram' ? 'Telegram' : 'Instagram'}
                    </span>
                  </div>
                  <div style={{ fontSize:13, color:C.dark, lineHeight:1.5 }}>
                    {h.content?.length > 200 ? h.content.slice(0, 200) + '…' : h.content}
                  </div>
                  {!h.success && h.error && (
                    <div style={{ fontSize:12, color:C.error, marginTop:4 }}>Error: {h.error}</div>
                  )}
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontSize:12, color:C.grey }}>{h.posted_at}</div>
                  {h.sent_count > 0 && (
                    <div style={{ fontSize:12, color:C.success, fontWeight:600, marginTop:4 }}>
                      ✓ {h.sent_count} reached
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Layout>
  );
}
